import { useLayoutEffect, useState } from 'react';
import axiosInstance from '../../config/axiosInstance';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://backend.rainbitx.com';
const configuredBackend = new URL(API_BASE_URL, window.location.origin);
const blobCache = new Map();
const inFlightRequests = new Map();

const isPrivateMedia = (src) => typeof src === 'string' && src.includes('/api/files/private/');

const resolvePrivateMedia = (src) => {
  if (!isPrivateMedia(src)) return null;
  try {
    const resolved = new URL(src, configuredBackend);
    return resolved.origin === configuredBackend.origin && resolved.pathname.startsWith('/api/files/private/')
      ? resolved.href
      : null;
  } catch {
    return null;
  }
};

const fetchPrivateBlob = (url, preview) => {
  const cacheKey = `${url}|${preview ? 'preview' : 'original'}`;
  if (blobCache.has(cacheKey)) return Promise.resolve(blobCache.get(cacheKey));
  if (inFlightRequests.has(cacheKey)) return inFlightRequests.get(cacheKey);

  const requestUrl = preview ? `${url}${url.includes('?') ? '&' : '?'}preview=1` : url;
  const request = axiosInstance.get(requestUrl, { responseType: 'blob' })
    .then((response) => {
      blobCache.set(cacheKey, response.data);
      return response.data;
    })
    .finally(() => inFlightRequests.delete(cacheKey));
  inFlightRequests.set(cacheKey, request);
  return request;
};

const LoadingMedia = () => (
  <span className='d-inline-flex align-items-center gap-2 text-muted small' role='status'>
    <span className='spinner-border spinner-border-sm' aria-hidden='true' />
    Loading media...
  </span>
);

const AuthenticatedMedia = ({ src, as = 'img', preview = true, openOnClick = false, showOpenButton = false, ...props }) => {
  const privateMedia = isPrivateMedia(src);
  const [state, setState] = useState(privateMedia ? 'loading' : 'ready');
  const [resolvedSrc, setResolvedSrc] = useState(privateMedia ? null : src);

  useLayoutEffect(() => {
    let objectUrl;
    let cancelled = false;

    if (!isPrivateMedia(src)) {
      setResolvedSrc(src);
      setState('ready');
      return undefined;
    }

    const privateUrl = resolvePrivateMedia(src);
    setResolvedSrc(null);
    setState('loading');
    if (!privateUrl) {
      setState('failed');
      return undefined;
    }

    const cacheKey = `${privateUrl}|${preview ? 'preview' : 'original'}`;
    const cachedBlob = blobCache.get(cacheKey);
    if (cachedBlob) {
      objectUrl = URL.createObjectURL(cachedBlob);
      setResolvedSrc(objectUrl);
      setState('ready');
      return () => URL.revokeObjectURL(objectUrl);
    }

    fetchPrivateBlob(privateUrl, preview)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('failed');
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, preview]);

  const openOriginal = async () => {
    if (!isPrivateMedia(src)) {
      window.open(src, '_blank', 'noopener,noreferrer');
      return;
    }
    const privateUrl = resolvePrivateMedia(src);
    if (!privateUrl) return;
    try {
      const blob = await fetchPrivateBlob(privateUrl, false);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch {
      setState('failed');
    }
  };

  if (state === 'loading') return <LoadingMedia />;
  if (state === 'failed') {
    return (
      <div className='border rounded p-3 text-center bg-light'>
        <p className='small text-muted mb-2'>Preview unavailable — original file retained</p>
        <button type='button' className='btn btn-sm btn-outline-primary' onClick={openOriginal}>
          Download original
        </button>
      </div>
    );
  }

  const Component = as;
  return (
    <>
      <Component src={resolvedSrc} {...props} onClick={openOnClick ? openOriginal : props.onClick} onError={() => setState('failed')} />
      {showOpenButton && <button type='button' className='btn btn-link btn-sm p-0 mt-1' onClick={openOriginal}>Open media in new tab</button>}
    </>
  );
};

export default AuthenticatedMedia;
